/* eslint-disable no-restricted-imports */
import React, { FC, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { addHideTabbarParam } from '../../../routing/post/utils/addHideTabbarParam';
import { isBloggerBusiness } from '../../../utils/accessService';
import { buildImageSource, buildImageSourceSet } from '../../../utils/buildImageSource';
import { getDomainFromUrl } from '../../../utils/getDomainFromUrl/getDomainFromUrl';
import { useExperimentFlag } from '../../../utils/useExperimentFlag';
import { useTranslation } from '../../../utils/useTranslation';
import { CHECKOUT_BUTTON_TEXT } from '../../constants';
import { IconArrowDiagonalRight } from '../../icon/IconArrowDiagonalRight';
import { IconProduct } from '../../icon/IconProduct';
import { IconPurchases } from '../../icon/IconPurchases';
import { Button } from '../../input/Button';
import { Box } from '../../layout/Box';
import { Flex } from '../../layout/Flex';
import { Image } from '../../media/Image';
import { MediaCover } from '../../media/MediaCover';
import { Price } from '../../price/Price';
import { ProductDomain } from '../../product/ProductDomain/ProductDomain';
import { Typography } from '../../typography/Typography';
import { PostProductsFixedContainer } from '../PostProductsFixedContainer/PostProductsFixedContainer';
import { ProductMonetization } from '../ProductMonetization/ProductMonetization';
import { ProductUnavailableLabel } from '../ProductUnavailableLabel/ProductUnavailableLabel';
import { PostSingleProductProps } from './types';

export const PostSingleProduct: FC<PostSingleProductProps> = ({
    product,
    business,
    onBuyOnSiteClick,
    onCatalogClick,
    hideTabbar,
    trackProductShown,
    trackButtonShown,
}) => {
    const t = useTranslation();
    const {
        priceInfo,
        oldPriceInfo,
        content,
        title,
        url,
        checkout,
        id,
        monetizationEnabled,
        fintechDiscount,
        isUnavailable,
    } = product;

    const media = content[0]?.media;

    const checkoutFlagValue = Number(useExperimentFlag('products_checkout'));
    const withCheckout = (checkoutFlagValue === 1 || checkoutFlagValue === 2) && checkout?.url;
    const productTargetUrl = withCheckout ? checkout?.url! : url;
    const domain = isBloggerBusiness(business?.type)
        ? getDomainFromUrl(product.originalUrl)
        : undefined;

    const [hasProductLogged, setHasProductLogged] = useState(false);
    const [hasBusinessLogged, setHasBusinessLogged] = useState(false);

    useEffect(() => {
        if (!hasProductLogged && product) {
            product?.id && trackProductShown?.(product.id, product.url);
            product?.url && trackButtonShown?.('BuyOnSite', product.url);
            setHasProductLogged(true);
        }
    }, [hasProductLogged, product, trackButtonShown, trackProductShown]);

    useEffect(() => {
        if (!hasBusinessLogged && business) {
            business?.siteUrl && trackButtonShown?.('GoToSite', business.siteUrl);
            business?.catalogUrl && trackButtonShown?.('Catalog', business.catalogUrl);
            setHasBusinessLogged(true);
        }
    }, [business, hasBusinessLogged, trackButtonShown]);

    return (
        <Flex direction="column" width="100%" height="100%">
            <MediaCover
                minHeight={200}
                ratio={1}
                overflow="hidden"
                width="100%"
                borderTopLeftRadius={3}
                borderTopRightRadius={3}
            >
                <Flex
                    alignItems="center"
                    justifyContent="center"
                    backgroundColor="var(--background-primary)"
                >
                    {media ? (
                        <Image
                            objectFit="contain"
                            width="100%"
                            height="100%"
                            src={buildImageSource(media)}
                            srcSet={buildImageSourceSet(media)}
                        />
                    ) : (
                        <IconProduct size={72} color="var(--control-secondary-background)" />
                    )}
                </Flex>
            </MediaCover>
            <Flex p={2} pb={0} width="100%">
                {monetizationEnabled && !isUnavailable ? (
                    <ProductMonetization color="invert" pb={1} />
                ) : null}
                {domain ? <ProductDomain domain={domain} pb={1} /> : null}
                <Typography variant="body" weight="regular" pb={1}>
                    {t(title)}
                </Typography>
                {isUnavailable ? <ProductUnavailableLabel widthHint variant="body" /> : null}
                {isUnavailable ? null : (
                    <Box maxWidth={250}>
                        <Price
                            variant="title3"
                            mb={0.25}
                            priceInfo={priceInfo}
                            oldPriceInfo={oldPriceInfo}
                            fintechDiscount={fintechDiscount}
                            oldPriceOptions={{ variant: 'body' }}
                            showOldPriceBadge
                        />
                    </Box>
                )}
            </Flex>
            <PostProductsFixedContainer p={2} mt="auto">
                <Flex width="100%" spacing={1}>
                    <Flex direction="row" width="100%" spacing={1}>
                        {withCheckout ? (
                            <Button
                                as="a"
                                href={url}
                                target="_blank"
                                size="l"
                                width="100%"
                                color="secondary"
                                onClick={() => onBuyOnSiteClick(id, url)}
                            >
                                {withCheckout ? t('На сайт') : t('Купить на сайте')}
                                <IconArrowDiagonalRight size={16} margin="0 0 0 8px" />
                            </Button>
                        ) : null}
                        {business?.catalogUrl ? (
                            <Button
                                size="l"
                                as={Link}
                                to={
                                    hideTabbar
                                        ? addHideTabbarParam(business.catalogUrl)
                                        : business.catalogUrl
                                }
                                width="100%"
                                color="secondary"
                                spacing={0.5}
                                onClick={onCatalogClick}
                            >
                                <IconProduct fontSize={20} />
                                {t(business.catalogTitle)}
                            </Button>
                        ) : null}
                    </Flex>
                    {isUnavailable ? null : (
                        <Button
                            as="a"
                            href={productTargetUrl}
                            target="_blank"
                            size="l"
                            width="100%"
                            color={withCheckout ? 'searchRed' : 'primary'}
                            onClick={() => onBuyOnSiteClick(id, productTargetUrl)}
                        >
                            {withCheckout ? <IconPurchases size={16} margin="0 8px 0 0" /> : null}
                            {withCheckout ? t(CHECKOUT_BUTTON_TEXT) : t('Купить на сайте')}
                        </Button>
                    )}
                </Flex>
            </PostProductsFixedContainer>
        </Flex>
    );
};
